/**
 * Since this class knows nothing about what the timer will be used for, exactly, this is a broad value, which is likely
 * not useful enough in any specific enough context. Callers should be strongly advised to set this based on their use
 * case.
 */
const DEFAULT_GENERAL_TIMEOUT = 10000

class TimeoutError extends Error {}

export type TimeoutOptions = {
  ms?: number
}

/**
 * NOTE: I implemented this as a class because after some rough benchmarks, this seemed to be the most efficient way to
 * make sure memory is cleaned up, and unlike an anonymous function approach, it doesn't close over lots of different
 * variables already defined in the calling lexical scope.
 */
class Timeout {
  // The return value of setTimeout in the browser is a number. In Node.js it's an object. Thus, we retrieve the type
  // for the timeout id from the return value of setTimeout. This way we're browser-vs.-Node.js-agnostic.
  private id: undefined | ReturnType<typeof setTimeout>
  private timeout: number | string

  constructor(options: TimeoutOptions) {
    this.timeout = options.ms || DEFAULT_GENERAL_TIMEOUT
    this.id = undefined
  }

  begin() {
    return new Promise((_resolve, reject) => {
      this.id = setTimeout(
        () => reject(new TimeoutError(`Call timed out at ${this.timeout}ms`)),
        Number(this.timeout)
      )
    })
  }

  end() {
    this.id && clearTimeout(this.id)
  }
}

const timeout = (
  promise: Promise<any>,
  ms: number = DEFAULT_GENERAL_TIMEOUT,
  controller: AbortController
): Promise<any> =>
  new Promise<any>(async (resolve, reject) => {
    const timeoutID = setTimeout(() => {
      reject(new TimeoutError(`Call timed out at ${ms}ms`))
      // This is the only abort condition in this utility. All other conditions are left to callers based on specific
      // logic that only they can know
      controller.abort()
    }, Number(ms))
    try {
      resolve(await promise)
    } catch (e) {
      reject(e)
    } finally {
      clearTimeout(timeoutID)
    }
  })

export default timeout
export { Timeout, TimeoutError }
